import type { MasterDataViewModel, CategoryGroup, KeyValue } from './masterData.types'
import { formatCurrency, formatDate } from '@/lib/utils'

type Raw = Record<string, unknown>
const obj = (v: unknown): Raw => (v && typeof v === 'object' ? (v as Raw) : {})
const str = (v: unknown): string => (typeof v === 'string' ? v : '')
const num = (v: unknown): number => (typeof v === 'number' ? v : 0)
const nonEmpty = (rows: KeyValue[]): KeyValue[] | undefined => (rows.length ? rows : undefined)

function parseCategories(raw: Raw): MasterDataViewModel['categories'] {
  const cats = Array.isArray(raw.Categories) ? (raw.Categories as Raw[]) : []
  const map = new Map<string, string[]>()
  for (const c of cats) {
    const parts = str(c.Name).split('->').map((p) => p.trim()).filter(Boolean)
    if (!parts.length) continue
    const [root, ...rest] = parts
    const leaf = rest.join(' › ')
    const arr = map.get(root) ?? []
    if (leaf) arr.push(leaf)
    map.set(root, arr)
  }
  if (!map.size) return undefined
  const groups: CategoryGroup[] = [...map.entries()].map(([root, leaves]) => ({ root, leaves }))
  return { groups, total: cats.length }
}

function parseIdentifiers(raw: Raw): KeyValue[] | undefined {
  const ids = obj(raw.Identifiers)
  const rows: KeyValue[] = []
  const push = (label: string, v: unknown) => { if (str(v)) rows.push({ label, value: str(v) }) }
  // ASIN(s): User-Vorgabe — alle ASINs aus dem Identifiers.Asins-Array anzeigen.
  const asins = Array.isArray(ids.Asins) ? (ids.Asins as unknown[]).map(String).filter(Boolean) : []
  if (asins.length) rows.push({ label: 'asins', value: asins.join(', ') })
  push('manufacturerNumber', ids.ManufacturerNumber)
  push('amazonFnsku', ids.AmazonFnsku)
  push('upc', ids.UPC)
  push('isbn', ids.ISBN)
  push('ownIdentifier', ids.OwnIdentifier)
  return nonEmpty(rows)
}

function parsePrices(raw: Raw): KeyValue[] | undefined {
  const p = obj(raw.ItemPriceData)
  const rows: KeyValue[] = []
  const push = (label: string, v: unknown) => { if (num(v) > 0) rows.push({ label, value: formatCurrency(num(v)) }) }
  // PurchasePriceNet (EK) bewusst ausgelassen — sensibel.
  push('salesPriceNet', p.SalesPriceNet)
  push('amazonPrice', p.AmazonPrice)
  push('ebayPrice', p.EbayPrice)
  push('suggestedRetailPrice', p.SuggestedRetailPrice)
  return nonEmpty(rows)
}

function parseDimensionsWeight(raw: Raw): KeyValue[] | undefined {
  const d = obj(raw.Dimensions), w = obj(raw.Weights)
  const rows: KeyValue[] = []
  const l = num(d.Length), wi = num(d.Width), h = num(d.Height)
  if (l || wi || h) rows.push({ label: 'dimensions', value: `${l} × ${wi} × ${h}`, unit: 'cm' })
  if (num(w.ShippingWeight) > 0) rows.push({ label: 'shippingWeight', value: String(num(w.ShippingWeight)), unit: 'kg' })
  if (num(w.ItemWeigth) > 0) rows.push({ label: 'itemWeight', value: String(num(w.ItemWeigth)), unit: 'kg' })
  return nonEmpty(rows)
}

function parseStock(raw: Raw): KeyValue[] | undefined {
  const s = obj(raw.StorageOptions)
  const rows: KeyValue[] = []
  if (num(s.ProcurementTime) > 0) rows.push({ label: 'procurementTime', value: String(num(s.ProcurementTime)), unit: 'days' })
  if (num(s.GlobalMinimumStockLevel) > 0) rows.push({ label: 'minStock', value: String(num(s.GlobalMinimumStockLevel)) })
  if ('InventoryManagementActive' in s) rows.push({ label: 'inventoryManagement', value: s.InventoryManagementActive ? 'active' : 'inactive' })
  if ('AllowNegativeStock' in raw) rows.push({ label: 'negativeStock', value: raw.AllowNegativeStock ? 'allowed' : 'notAllowed' })
  return nonEmpty(rows)
}

function parseStatus(raw: Raw): KeyValue[] | undefined {
  const rows: KeyValue[] = []
  if ('IsActive' in raw) rows.push({ label: 'active', value: raw.IsActive ? 'yes' : 'no' })
  if (str(raw.Added)) rows.push({ label: 'addedAt', value: formatDate(str(raw.Added)) })
  if (str(raw.Changed)) rows.push({ label: 'changedAt', value: formatDate(str(raw.Changed)) })
  return nonEmpty(rows)
}

export function parseJtlMasterData(raw: Record<string, unknown> | null | undefined): MasterDataViewModel {
  const r = obj(raw)
  const channels = Array.isArray(r.ActiveSalesChannels)
    ? (r.ActiveSalesChannels as unknown[]).map(String)
    : []
  return {
    categories: parseCategories(r),
    identifiers: parseIdentifiers(r),
    prices: parsePrices(r),
    dimensionsWeight: parseDimensionsWeight(r),
    stockProcurement: parseStock(r),
    salesChannels: channels.length ? channels : undefined,
    status: parseStatus(r),
  }
}
